// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import Modal from '../ui/Modal';
import { transactionAPI } from '../../api/services/budget/transactionService';
import paymentAPI from '../../api/services/budget/paymentService';
import { TRANSACTION_TYPES } from '../../utils/constants';
import { formatDate } from '../../utils/dates';
import { formatINR } from '../../utils/currency';

const MATCH_MODES = [
  { id: 'group', label: 'Type + Category' },
  { id: 'keyword', label: 'Description contains' },
  { id: 'regex', label: 'Description matches (regex)' },
];

const FIELD_OPTIONS = [
  { id: 'category', label: 'Category' },
  { id: 'subcategory', label: 'Subcategory' },
  { id: 'payment_mode', label: 'Payment mode' },
  { id: 'transaction_type', label: 'Type' },
  { id: 'is_regular', label: 'Regular (yes/no)' },
];

const errorText = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (data.non_field_errors) return data.non_field_errors[0];
  if (typeof data === 'object') return Object.values(data).flat()[0] || fallback;
  return fallback;
};

/**
 * Group transactions by type/category or by a description match (plain
 * keyword or regex), preview the group, then bulk-apply a field change (#14).
 * `categories` is the list already loaded on Transactions.jsx (id, name,
 * category_type, subcategories[]).
 */
export default function GroupUpdateModal({ isOpen, onClose, categories, onApplied }) {
  // ── Match spec ──────────────────────────────────────────────────
  const [matchMode, setMatchMode] = useState('group');
  const [matchType, setMatchType] = useState('');
  const [matchCategory, setMatchCategory] = useState('');
  const [matchSubcategory, setMatchSubcategory] = useState('');
  const [keyword, setKeyword] = useState('');
  const [regex, setRegex] = useState('');

  // ── Target ──────────────────────────────────────────────────────
  const [field, setField] = useState('category');
  const [value, setValue] = useState('');
  const [payModes, setPayModes] = useState([]);

  // ── Preview / apply state ────────────────────────────────────────
  const [preview, setPreview] = useState(null); // { matched_count, sample }
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    paymentAPI
      .listModes()
      .then((res) => setPayModes(Array.isArray(res.data) ? res.data : (res.data?.results ?? [])))
      .catch(() => setPayModes([]));
  }, [isOpen]);

  // Any change to the match spec or target invalidates the last preview —
  // force a fresh one before Apply can be used.
  useEffect(() => {
    setPreview(null);
  }, [matchMode, matchType, matchCategory, matchSubcategory, keyword, regex, field, value]);

  const reset = () => {
    setMatchMode('group');
    setMatchType('');
    setMatchCategory('');
    setMatchSubcategory('');
    setKeyword('');
    setRegex('');
    setField('category');
    setValue('');
    setPreview(null);
    setError('');
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const buildSpec = (dryRun) => {
    const spec = { dry_run: dryRun, field };
    if (matchMode === 'group') {
      if (matchType) spec.match_type = matchType;
      if (matchCategory) spec.match_category = Number(matchCategory);
      if (matchSubcategory) spec.match_subcategory = Number(matchSubcategory);
    } else if (matchMode === 'keyword') {
      spec.keyword = keyword;
    } else {
      spec.regex = regex;
    }
    spec.value = field === 'is_regular' ? value === 'true' : Number(value) || value;
    return spec;
  };

  const hasMatchCriteria =
    matchMode === 'group' ? !!(matchType || matchCategory) : matchMode === 'keyword' ? !!keyword : !!regex;
  const hasTarget = field === 'is_regular' ? value !== '' : !!value;

  const handlePreview = async () => {
    setPreviewing(true);
    setError('');
    try {
      const res = await transactionAPI.bulkUpdate(buildSpec(true));
      setPreview(res.data);
    } catch (err) {
      setError(errorText(err, 'Failed to preview the match.'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setError('');
    try {
      const res = await transactionAPI.bulkUpdate(buildSpec(false));
      onApplied?.(res.data);
      close();
    } catch (err) {
      setError(errorText(err, 'Failed to apply the update.'));
    } finally {
      setApplying(false);
    }
  };

  const categoryOptions = matchType
    ? categories.filter((c) => c.category_type === matchType)
    : categories;
  const matchCategorySubs =
    categories.find((c) => String(c.id) === matchCategory)?.subcategories ?? [];
  // The target category isn't constrained to the match's category/type —
  // e.g. reassigning miscategorized EXPENSE rows into a SAVING category.
  const targetCategoryOptions = categories;

  return (
    <Modal isOpen={isOpen} onClose={close} title="Group update" size="lg">
      <div className="space-y-5">
        {/* ── Match spec ──────────────────────────────────────────── */}
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Group by</p>
          <div className="flex gap-2 flex-wrap mb-3">
            {MATCH_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMatchMode(m.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  matchMode === m.id
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {matchMode === 'group' ? (
            <div className="grid grid-cols-2 gap-3">
              <select
                value={matchType}
                onChange={(e) => {
                  setMatchType(e.target.value);
                  setMatchCategory('');
                  setMatchSubcategory('');
                }}
                className="px-2 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="">Any type</option>
                {TRANSACTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <select
                value={matchCategory}
                onChange={(e) => {
                  setMatchCategory(e.target.value);
                  setMatchSubcategory('');
                }}
                className="px-2 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="">Any category</option>
                {categoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {matchCategorySubs.length > 0 && (
                <select
                  value={matchSubcategory}
                  onChange={(e) => setMatchSubcategory(e.target.value)}
                  className="px-2 py-2 text-sm border border-gray-200 rounded-lg col-span-2"
                >
                  <option value="">Any subcategory</option>
                  {matchCategorySubs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : matchMode === 'keyword' ? (
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g. swiggy"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          ) : (
            <input
              type="text"
              value={regex}
              onChange={(e) => setRegex(e.target.value)}
              placeholder="e.g. ^UPI/.*swiggy"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono"
            />
          )}
        </div>

        {/* ── Target ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Set</p>
            <select
              value={field}
              onChange={(e) => {
                setField(e.target.value);
                setValue('');
              }}
              className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg"
            >
              {FIELD_OPTIONS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">to</p>
            {field === 'category' && (
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="">Select…</option>
                {targetCategoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            {field === 'subcategory' && (
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="">Select…</option>
                {(matchCategory ? matchCategorySubs : categories.flatMap((c) => c.subcategories ?? [])).map(
                  (s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ),
                )}
              </select>
            )}
            {field === 'payment_mode' && (
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="">Select…</option>
                {payModes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
            {field === 'transaction_type' && (
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="">Select…</option>
                {TRANSACTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            )}
            {field === 'is_regular' && (
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="">Select…</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            )}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {/* ── Preview ─────────────────────────────────────────────── */}
        {preview && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 flex items-center gap-1.5">
              <Layers size={13} />
              {preview.matched_count} transaction{preview.matched_count !== 1 ? 's' : ''} match
              {preview.sample.length < preview.matched_count &&
                ` (showing first ${preview.sample.length})`}
            </div>
            {preview.sample.length > 0 && (
              <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                {preview.sample.map((t) => (
                  <div key={t.id} className="px-3 py-1.5 text-xs flex justify-between gap-2">
                    <span className="text-gray-500 shrink-0">{formatDate(t.date)}</span>
                    <span className="text-gray-700 truncate flex-1">{t.description || '—'}</span>
                    <span className="text-gray-900 font-medium shrink-0">{formatINR(t.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Actions ─────────────────────────────────────────────── */}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={close}
            className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePreview}
            disabled={!hasMatchCriteria || !hasTarget || previewing}
            className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {previewing ? 'Previewing…' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!preview || preview.matched_count === 0 || applying}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applying ? 'Applying…' : preview ? `Apply to ${preview.matched_count}` : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
